/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, ReactNode } from 'react'
import { SideNavContext } from './SideNavContext'
import { PageState } from '../../../contexts/page-state'

interface SideNavStateProps {
  children: ReactNode
  collapseCallback?: (isCollapsed: boolean) => void
}

export function SideNavState({ children, collapseCallback }: SideNavStateProps) {
  const [activeMenuSection, setActiveMenuSection] = useState<React.RefObject<HTMLAnchorElement | null> | null>(null)
  const [activeMenuItem, setActiveMenuItem] = useState<React.RefObject<HTMLElement | null> | null>(null)
  const [expandedMenuSection, setExpandedMenuSection] = useState<React.RefObject<HTMLElement | null> | null>(null)
  const [isMenuCollapsed, setIsMenuCollapsed] = useState<boolean>(false)

  useEffect(() => {
    // Get collapse state from localStorage
    const isCollapsed = localStorage.getItem('side-nav-collapse') === 'true'
    if (isCollapsed) {
      changeCollapseTo(true)
    }

    // Handle window resize events
    const BREAKING_WIDTH = 570
    let lastResizeWidth = window.innerWidth

    const handleResize = () => {
      const currentWidth = window.innerWidth
      if (currentWidth > BREAKING_WIDTH && lastResizeWidth < BREAKING_WIDTH) {
        expandMenu()
      } else if (currentWidth < BREAKING_WIDTH) {
        collapseMenu()
      }
      lastResizeWidth = currentWidth
    }

    // Debounce resize events
    let timeoutId: NodeJS.Timeout
    const debouncedHandleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleResize, 200)
    }

    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      clearTimeout(timeoutId)
    }
  }, [])

  function collapseMenu() {
    changeCollapseTo(true)
  }

  function expandMenu() {
    changeCollapseTo(false)
  }

  function changeCollapseTo(collapsed: boolean) {
    if (collapseCallback) {
      collapseCallback(collapsed)
    }
    setIsMenuCollapsed(collapsed)
    localStorage.setItem('side-nav-collapse', collapsed.toString())
  }

  return (
    <PageState>
      <SideNavContext.Provider
        value={{
          isMenuCollapsed,
          activeMenuSection,
          activeMenuItem,
          expandedMenuSection,
          setActiveMenuSection,
          setExpandedMenuSection,
          setActiveMenuItem,
          expandMenu,
          collapseMenu
        }}
      >
        {children}
      </SideNavContext.Provider>
    </PageState>
  )
}
